import { Component, Injector, OnInit, TemplateRef, Type, ViewChild } from '@angular/core';
import { PagedRequestDto } from '../../../xui/x-page/x-pg-model/x-pg.model';
import { XPgListComponent } from '../../../xui/x-page/x-pg-list/x-pg-list.component';
import { ListService, mapEnumToOptions } from '@abp/ng.core';
import { LedgerCodeDto, LedgerCodeService, LedgerType, UpdateLedgerCodeDto } from '@proxy/ledgers';
import { ActivatedRoute } from '@angular/router';
import { ChargeItemSectionDto, ChargeItemSectionService } from '@proxy/charge-item-sections';
import {
  BoatChargeItemSectionDto,
  BoatChargeItemSectionService,
} from '@proxy/boat-charge-item-sections';
import { NzMessageService } from 'ng-zorro-antd/message';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-ledger-code',
  templateUrl: './ledger-code.component.html',
  styleUrls: ['./ledger-code.component.scss'],
})
export class LedgerCodeComponent implements OnInit {
  @ViewChild('template') template: TemplateRef<any>;

  tableHeight = 535;

  searchValue = '';
  filteredLedgerCodes: LedgerCodeDto[] = [];
  columnFilters: { [key: string]: string } = {};
  showColumnFilters = false;

  ledgerCodeForm: FormGroup;
  ledgerTypeOptions = mapEnumToOptions(LedgerType);

  boatChargeItemSections: BoatChargeItemSectionDto[];
  chargeItemSections: ChargeItemSectionDto[] = [];
  filteredSections: any[] = [];
  isEditMode = false;
  selectedLedgerCode: any = null;

  component: Type<any>;
  injector: Injector;
  instance: XPgListComponent;

  isVisible = false;
  isOkLoading = false;

  companyId: string;

  ledgerCodes: LedgerCodeDto[] = [];

  list: ListService<PagedRequestDto>;

  constructor(
    private formBuilder: FormBuilder,
    activatedRoute: ActivatedRoute,
    private ledgerCodeService: LedgerCodeService,
    private nzMessageService: NzMessageService,
    private chargeItemSectionService: ChargeItemSectionService,
    private boatChargeItemSectionService: BoatChargeItemSectionService
  ) {
    activatedRoute.params.subscribe(() => {
      this.companyId = activatedRoute.snapshot.parent.params.id;
    });
  }

  ngOnInit(): void {
    this.updateTableHeight();
    this.getLedgerCodes();
    this.initializeForm();
    this.getChargeItemSections();
    this.getBoatChargeItemSections();
  }

  initializeForm() {
    this.ledgerCodeForm = this.formBuilder.group({
      companyId: this.companyId,
      ledgerType: [null, Validators.required],
      sectionId: [null, Validators.required],
      sectionName: [null, Validators.required],
      d365Code: ['', Validators.required],
    });
  }

  getLedgerCodes() {
    this.ledgerCodeService
      .getList({
        filter: `companyId eq "${this.companyId}"`,
        ignoreMaxResultCount: true,
      } as PagedRequestDto)
      .subscribe(response => {
        this.ledgerCodes = response.items;
        this.filteredLedgerCodes = [...response.items];
      });
  }

  getChargeItemSections() {
    this.chargeItemSectionService
      .getList({ ignoreMaxResultCount: true } as PagedRequestDto)
      .subscribe(response => {
        this.chargeItemSections = response.items;
      });
  }

  getBoatChargeItemSections() {
    this.boatChargeItemSectionService
      .getList({ ignoreMaxResultCount: true } as PagedRequestDto)
      .subscribe(response => {
        this.boatChargeItemSections = response.items;
      });
  }

  onSearch(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase().trim();
    this.searchValue = searchTerm;
    this.applyFilters();
  }

  showModal(): void {
    this.isEditMode = false;
    this.selectedLedgerCode = null;
    this.ledgerCodeForm.reset({
      companyId: this.companyId,
    });
    this.ledgerCodeForm.patchValue({
      ledgerType: null,
      sectionId: null,
      sectionName: null,
      d365Code: '',
    });

    this.isVisible = true;
  }

  toggleColumnFilters(): void {
    this.showColumnFilters = !this.showColumnFilters;
    if (!this.showColumnFilters) {
      this.columnFilters = {};
      this.applyFilters();
    }
  }

  updateColumnFilter(column: string, event: Event): void {
    const value = (event.target as HTMLInputElement).value.toLowerCase().trim();

    if (value) {
      this.columnFilters[column] = value;
    } else {
      delete this.columnFilters[column];
    }

    this.applyFilters();
  }

  applyFilters(): void {
    let filteredData = [...this.ledgerCodes];
    if (this.searchValue) {
      filteredData = filteredData.filter(
        code =>
          (code.sectionName?.toLowerCase() || '').includes(this.searchValue) ||
          (code.d365Code?.toLowerCase() || '').includes(this.searchValue) ||
          (LedgerType[code.ledgerType]?.toLowerCase() || '').includes(this.searchValue)
      );
    }
    Object.keys(this.columnFilters).forEach(column => {
      const filterValue = this.columnFilters[column];

      switch (column) {
        case 'ledgerType':
          filteredData = filteredData.filter(code =>
            (this.getLedgerTypeLabel(code.ledgerType)?.toLowerCase() || '').includes(filterValue)
          );
          break;
        case 'sectionId':
          filteredData = filteredData.filter(code =>
            (code.sectionId?.toLowerCase() || '').includes(filterValue)
          );
          break;
        case 'sectionName':
          filteredData = filteredData.filter(code =>
            (code.sectionName?.toLowerCase() || '').includes(filterValue)
          );
          break;
        case 'd365Code':
          filteredData = filteredData.filter(code =>
            (code.d365Code?.toLowerCase() || '').includes(filterValue)
          );
          break;
      }
    });

    this.filteredLedgerCodes = filteredData;
  }

  editLedgerCode(ledgerCode: LedgerCodeDto): void {
    this.isEditMode = true;
    this.selectedLedgerCode = ledgerCode;
    if (
      ledgerCode.ledgerType === LedgerType.Revenue ||
      ledgerCode.ledgerType === LedgerType.Expense
    ) {
      this.filteredSections = this.chargeItemSections;
    } else if (ledgerCode.ledgerType === LedgerType.OwnBoatRevenue) {
      this.filteredSections = this.chargeItemSections.filter(section => section.ownEntity === true);
    } else if (ledgerCode.ledgerType === LedgerType.BoatExpense) {
      this.filteredSections = this.boatChargeItemSections;
    }

    this.ledgerCodeForm.reset();

    this.ledgerCodeForm.setValue({
      companyId: ledgerCode.companyId,
      ledgerType: ledgerCode.ledgerType,
      sectionId: ledgerCode.sectionId,
      sectionName: ledgerCode.sectionName,
      d365Code: ledgerCode.d365Code,
    });
    this.isVisible = true;
  }

  saveLedgerCode(): void {
    if (this.ledgerCodeForm.invalid) {
      this.nzMessageService.error('Please fill all required fields!');
      return;
    }
    this.isOkLoading = true;
    const input = { ...this.ledgerCodeForm.value, companyId: this.companyId };
    this.ledgerCodeService.create(input).subscribe({
      next: () => {
        this.nzMessageService.success('Ledger Code added successfully!');
        this.isVisible = false;
        this.ngOnInit();
      },
      error: () => {
        this.isOkLoading = false;
        this.nzMessageService.error('Failed to add Ledger Code');
      },
    });
  }

  updateLedgerCode() {
    if (this.ledgerCodeForm.valid && this.selectedLedgerCode) {
      const formValues = this.ledgerCodeForm.value;

      const updateDto: UpdateLedgerCodeDto = {
        companyId: formValues.companyId,
        sectionId: formValues.sectionId,
        sectionName: formValues.sectionName,
        d365Code: formValues.d365Code,

        ledgerType:
          formValues.ledgerType !== null && formValues.ledgerType !== undefined
            ? Number(formValues.ledgerType)
            : this.selectedLedgerCode.ledgerType,

        concurrencyStamp: this.selectedLedgerCode.concurrencyStamp,
      };

      this.ledgerCodeService.update(this.selectedLedgerCode.id, updateDto).subscribe({
        next: (response: LedgerCodeDto) => {
          const index = this.filteredLedgerCodes.findIndex(l => l.id === response.id);
          if (index !== -1) {
            this.ledgerCodes = this.ledgerCodes.map(item =>
              item.id === response.id ? response : item
            );
            this.filteredLedgerCodes[index] = response;
            this.nzMessageService.success('Ledger code updated successfully.');
          }
          this.isVisible = false;
        },
        error: error => {
          this.nzMessageService.error('Failed to update Ledger code');
          console.error(`Failed to update: ${error.status} ${error.statusText}`);
        },
      });
    }
  }

  deleteLedgerCode(id: string): void {
    if (!id) {
      this.nzMessageService.error('Invalid ledger code ID');
      return;
    }
    this.ledgerCodeService.delete(id).subscribe({
      next: () => {
        this.filteredLedgerCodes = this.filteredLedgerCodes.filter(code => code.id !== id);
        this.ledgerCodes = this.ledgerCodes.filter(code => code.id !== id);
        this.nzMessageService.success('Ledger code deleted successfully');
      },
      error: error => {
        this.nzMessageService.error('Failed to delete ledger code');
        console.error('Error deleting ledger code:', error);
      },
    });
  }

  handleSave(): void {
    if (this.isEditMode && this.selectedLedgerCode) {
      this.updateLedgerCode();
    } else {
      this.saveLedgerCode();
    }
  }

  handleCancel(): void {
    this.isVisible = false;
    this.isEditMode = false;
    this.selectedLedgerCode = null;
  }

  onLedgerTypeChange() {
    const selectedType = this.ledgerCodeForm.get('ledgerType')?.value;
    if (selectedType === LedgerType.Revenue || selectedType === LedgerType.Expense) {
      this.filteredSections = this.chargeItemSections;
    } else if (selectedType === LedgerType.OwnBoatRevenue) {
      this.filteredSections = this.chargeItemSections.filter(section => section.ownEntity === true);
    } else if (selectedType === LedgerType.BoatExpense) {
      this.filteredSections = this.boatChargeItemSections;
    }
    this.ledgerCodeForm.get('sectionId')?.reset();
    this.ledgerCodeForm.get('sectionId')?.enable();
  }

  getLedgerTypeLabel(value: number): string {
    const matched = this.ledgerTypeOptions.find(option => option.value === value);
    return matched ? matched.key : 'Unknown';
  }

  onSectionChange() {
    const selectedSectionId = this.ledgerCodeForm.get('sectionId')?.value;
    const selected = this.filteredSections.find(section => section.id === selectedSectionId);
    this.ledgerCodeForm.patchValue({ sectionName: selected ? selected.name : '' });
  }

  updateTableHeight() {
    const screenHeight = window.innerHeight;

    if (screenHeight < 1080) {
      this.tableHeight = 535;
    } else if (screenHeight > 1080) {
      this.tableHeight = 750;
    }
  }

  sortLedgerType = (a: any, b: any) => a.ledgerType.localeCompare(b.ledgerType);
  sortSection = (a: any, b: any) => a.section.localeCompare(b.section);
  sortDetails = (a: any, b: any) => a.details.localeCompare(b.details);
  sortD365Code = (a: any, b: any) => a.d365Code.localeCompare(b.d365Code);
}
